import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { usePopup } from "../../context/PopupContext";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Pagination";
import "./AdminOrderReports.css";

const PER_PAGE = 10;

const REASON_LABELS = {
  marked_delivered_not_received: "Marked delivered but not received",
  marked_paid_not_delivered: "Marked paid/delivered but not delivered",
  wrong_items: "Wrong or missing items",
  quality_issue: "Quality issue",
  other: "Other",
};

const AdminOrderReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [modalStatus, setModalStatus] = useState("");
  const [modalNotes, setModalNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchReports();
        }
      } catch (error) {
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reports");
      setReports(res.data?.reports || []);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchReports();
  }, [user?.role]);

  const openModal = (report) => {
    setSelectedReport(report);
    setModalStatus(report.status || "pending");
    setModalNotes(report.adminNotes || "");
  };

  const closeModal = () => {
    setSelectedReport(null);
  };

  const handleSave = async () => {
    if (!selectedReport) return;
    setSaving(true);
    try {
      await api.patch(`/admin/reports/${selectedReport._id}`, {
        status: modalStatus,
        adminNotes: modalNotes,
      });
      showToast(t("adminOrderReports.reportUpdated"), "success");
      fetchReports();
      closeModal();
    } catch (err) {
      showToast(
        err.response?.data?.message || t("adminOrderReports.updateFailed"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const getReasonLabel = (reason) => REASON_LABELS[reason] || reason || "—";

  const filteredReports =
    filterStatus === "all"
      ? reports
      : reports.filter((r) => r.status === filterStatus);

  const totalPages = Math.ceil(filteredReports.length / PER_PAGE);
  const pageReports = filteredReports.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  const stats = {
    total: reports.length,
    pending: reports.filter((r) => r.status === "pending").length,
    under_review: reports.filter((r) => r.status === "under_review").length,
    resolved: reports.filter((r) => r.status === "resolved").length,
  };

  return (
    <AdminLayout>
      <div className="admin-order-reports-content">
        <div className="admin-order-reports-page-header">
          <div>
            <h1 className="admin-order-reports-page-title">
              {t("adminOrderReports.title")}
            </h1>
            <p className="admin-order-reports-page-subtitle">
              {t("adminOrderReports.subtitle")}
            </p>
          </div>
        </div>

        <div className="admin-order-reports-stats-grid">
          <div className="admin-order-reports-stat-card">
            <div className="admin-order-reports-stat-value">{stats.total}</div>
            <div className="admin-order-reports-stat-label">
              {t("adminOrderReports.total")}
            </div>
          </div>
          <div className="admin-order-reports-stat-card warning">
            <div className="admin-order-reports-stat-value">
              {stats.pending}
            </div>
            <div className="admin-order-reports-stat-label">
              {t("adminOrderReports.pending")}
            </div>
          </div>
          <div className="admin-order-reports-stat-card info">
            <div className="admin-order-reports-stat-value">
              {stats.under_review}
            </div>
            <div className="admin-order-reports-stat-label">
              {t("adminOrderReports.underReview")}
            </div>
          </div>
          <div className="admin-order-reports-stat-card success">
            <div className="admin-order-reports-stat-value">
              {stats.resolved}
            </div>
            <div className="admin-order-reports-stat-label">
              {t("adminOrderReports.resolved")}
            </div>
          </div>
        </div>

        <div className="admin-order-reports-filters">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="admin-order-reports-filter-select"
          >
            <option value="all">All status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under review</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        <div className="admin-order-reports-table-card">
          {loading ? (
            <div className="admin-order-reports-loading">
              <div className="admin-order-reports-spinner" />
              <p>{t("adminOrderReports.loading")}</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="admin-order-reports-empty">
              <p>{t("adminOrderReports.noReports")}</p>
            </div>
          ) : (
            <div className="admin-order-reports-table-wrap">
              <table className="admin-order-reports-table">
                <thead>
                  <tr>
                    <th>{t("adminOrderReports.order")}</th>
                    <th>{t("adminOrderReports.reporter")}</th>
                    <th>{t("adminOrderReports.reason")}</th>
                    <th>{t("adminOrderReports.description")}</th>
                    <th>{t("adminOrderReports.status")}</th>
                    <th>{t("adminOrderReports.date")}</th>
                    <th>{t("adminOrderReports.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageReports.map((r) => (
                    <tr key={r._id}>
                      <td>
                        <Link
                          to={`/admin/orders/${r.order?._id}`}
                          className="admin-order-reports-order-link"
                        >
                          {r.order?._id?.slice(-8) || "—"}
                        </Link>
                      </td>
                      <td>
                        <div className="admin-order-reports-reporter">
                          <span>{r.reportedBy?.name || "—"}</span>
                          <span className="admin-order-reports-email">
                            {r.reportedBy?.email}
                          </span>
                        </div>
                      </td>
                      <td>{getReasonLabel(r.reason)}</td>
                      <td className="admin-order-reports-desc-cell">
                        {(r.description || "").slice(0, 50)}
                        {(r.description || "").length > 50 ? "…" : ""}
                      </td>
                      <td>
                        <span
                          className={`admin-order-reports-status-badge ${r.status}`}
                        >
                          {r.status === "pending" && "Pending"}
                          {r.status === "under_review" && "Under review"}
                          {r.status === "resolved" && "Resolved"}
                        </span>
                      </td>
                      <td>
                        {r.createdAt
                          ? new Date(r.createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-order-reports-btn-view"
                          onClick={() => openModal(r)}
                        >
                          {t("adminOrderReports.view")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {selectedReport && (
        <div className="admin-order-reports-modal-overlay" onClick={closeModal}>
          <div
            className="admin-order-reports-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-order-reports-modal-header">
              <h2>{t("adminOrderReports.reportDetails")}</h2>
              <button
                type="button"
                className="admin-order-reports-modal-close"
                onClick={closeModal}
                aria-label={t("adminOrderReports.close")}
              >
                <FaTimes />
              </button>
            </div>
            <div className="admin-order-reports-modal-body">
              <p>
                <strong>Order:</strong>{" "}
                <Link
                  to={`/admin/orders/${selectedReport.order?._id}`}
                  onClick={closeModal}
                >
                  {selectedReport.order?._id?.slice(-8) || "—"}
                </Link>
              </p>
              <p>
                <strong>Reporter:</strong> {selectedReport.reportedBy?.name} (
                {selectedReport.reportedBy?.email})
              </p>
              <p>
                <strong>Reason:</strong> {getReasonLabel(selectedReport.reason)}
              </p>
              <p>
                <strong>Description:</strong>
              </p>
              <div className="admin-order-reports-modal-description">
                {selectedReport.description}
              </div>
              <div className="admin-order-reports-modal-form">
                <label>Status</label>
                <select
                  value={modalStatus}
                  onChange={(e) => setModalStatus(e.target.value)}
                  className="admin-order-reports-modal-select"
                >
                  <option value="pending">Pending</option>
                  <option value="under_review">Under review</option>
                  <option value="resolved">Resolved</option>
                </select>
                <label>{t("adminOrderReports.adminNotes")}</label>
                <textarea
                  value={modalNotes}
                  onChange={(e) => setModalNotes(e.target.value)}
                  className="admin-order-reports-modal-notes"
                  rows={4}
                  placeholder={t("adminOrderReports.notesPlaceholder")}
                />
              </div>
            </div>
            <div className="admin-order-reports-modal-footer">
              <button
                type="button"
                className="admin-order-reports-btn-cancel"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-order-reports-btn-save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t("adminOrderReports.saving") : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrderReports;
